import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';
const FORM_URL = 'https://readdy.ai/api/form/d785urprcamcd36dau00';

export default function ConsultationPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = '무료 전문가 상담 신청 | 필그린 인테리어 강남';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', '필그린 인테리어 무료 전문가 상담 신청. 1:1 전담 디자이너 배정, 현장 방문 상담, 무료 설계 제안. 신청 후 1~2 영업일 내 연락.');
    setMeta('og:title', '무료 전문가 상담 신청 | 필그린 인테리어 강남', true);
    setMeta('og:url', `${SITE_URL}/consultation`, true);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '필그린 인테리어 무료 전문가 상담',
      description: '1:1 전담 디자이너 배정, 현장 방문 상담, 무료 설계 제안 서비스.',
      url: `${SITE_URL}/consultation`,
      provider: {
        '@type': 'LocalBusiness',
        name: '필그린 인테리어',
        url: SITE_URL,
        telephone: '010-5350-7926',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'KRW',
        description: '무료 인테리어 상담',
      },
      areaServed: '서울특별시 강남구',
    };

    const scriptId = 'jsonld-consultation';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(FORM_URL, { method: 'POST', body: data, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      setSubmitted(true);
    } catch {
      alert('제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f7f5f3] min-h-screen">
      <Navbar />
      {/* Hero */}
      <section className="relative h-72 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/a7af675e26da1ddefe779bdb5b9e0441.jpg" alt="상담" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-12 w-full">
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Consultation</span>
          <h1 className="text-3xl md:text-5xl font-light text-white mt-2 leading-snug md:leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>전문가 상담</h1>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left Info */}
          <div className="lg:col-span-2">
            <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Free Consultation</span>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mt-4 mb-5 md:mb-6 leading-snug" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              무료 전문가<br />상담 신청
            </h2>
            <p className="text-[#555] text-sm leading-relaxed mb-8">
              feel green의 전문 디자이너가 고객님의 공간을 직접 분석하고 최적의 인테리어 솔루션을 제안해 드립니다. 상담은 완전 무료이며, 부담 없이 신청하세요.
            </p>
            <div className="space-y-5">
              {[
                { icon: 'ri-user-star-line', title: '1:1 전담 디자이너 배정', desc: '경력 10년 이상의 전문 디자이너가 처음부터 끝까지 담당합니다.' },
                { icon: 'ri-home-smile-line', title: '현장 방문 상담', desc: '직접 현장을 방문하여 정확한 상태를 파악하고 맞춤 제안을 드립니다.' },
                { icon: 'ri-file-list-3-line', title: '무료 설계 제안', desc: '상담 후 기본 설계안과 예상 견적을 무료로 제공해 드립니다.' },
                { icon: 'ri-time-line', title: '빠른 응답', desc: '신청 후 1~2 영업일 내에 담당자가 연락드립니다.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#c9a84c]/10 shrink-0">
                    <i className={`${item.icon} text-[#c9a84c] text-lg`} />
                  </div>
                  <div>
                    <h4 className="text-[#1a1a1a] font-semibold text-sm mb-1">{item.title}</h4>
                    <p className="text-[#777] text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#e8e4dc] h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#c9a84c]/10 mx-auto mb-4">
                  <i className="ri-check-line text-[#c9a84c] text-3xl" />
                </div>
                <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">상담 신청이 완료되었습니다!</h3>
                <p className="text-[#666] text-sm">1~2 영업일 내에 담당자가 연락드리겠습니다.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 border border-[#e8e4dc] space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">이름 *</label>
                    <input name="name" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="홍길동" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">연락처 *</label>
                    <input name="phone" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="010-0000-0000" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">이메일</label>
                  <input name="email" type="email" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="example@email.com" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">상담 유형 *</label>
                    <select name="consultation_type" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] bg-white cursor-pointer">
                      <option value="">선택해주세요</option>
                      {['아파트 인테리어', '빌라 인테리어', '오피스텔 인테리어', '상업공간', '사무실', '기타'].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">상담 방법 *</label>
                    <select name="method" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] bg-white cursor-pointer">
                      <option value="">선택해주세요</option>
                      {['전화 상담', '방문 상담', '온라인 화상 상담'].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">희망 상담 일시</label>
                  <input name="preferred_date" type="date" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">상담 내용 *</label>
                  <textarea name="message" required maxLength={500} rows={5} className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] resize-none" placeholder="상담하고 싶은 내용을 자유롭게 작성해주세요. (최대 500자)" />
                </div>
                <button type="submit" disabled={submitting} className="w-full bg-[#c9a84c] text-[#0d0d0d] font-bold py-4 rounded-full text-sm cursor-pointer hover:bg-[#b8973b] transition-colors whitespace-nowrap disabled:opacity-60">
                  {submitting ? '제출 중...' : '상담 신청하기'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
